import { cva, type VariantProps } from "class-variance-authority";

import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

export type ButtonOrSpanProps =
  | (React.ComponentProps<"button"> & {
      asButton: true;
    })
  | (React.ComponentProps<"span"> & {
      asButton?: false;
    });

export type TrackingTagProps = ButtonOrSpanProps &
  VariantProps<typeof tagVariants> & {
    isLoading?: boolean;
    dataTestId?: string;
  };

const tagVariants = cva(
  "inline-flex items-center justify-center rounded-xs px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden",
  {
    variants: {
      variant: {
        default: "border",
        neutral: "bg-background/component/10",
        positive: "bg-text/positive/20",
        negative: "bg-text/negative/20",
        pending: "bg-text/pending/20",
        warning: "bg-text/warning/20",
        disabled: "bg-text/disabled/20 text-text/secondary",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90",
        destructive:
          "border-transparent bg-destructive text-white [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        "data-1": "bg-data-1/20",
        "data-2": "bg-data-2/20",
        "data-3": "bg-data-3/20",
        "data-4": "bg-data-4/20",
        "data-5": "bg-data-5/20",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export const TrackingTag = ({
  children,
  className,
  dataTestId = "tag",
  isLoading = false,
  variant,
  asButton = false,
  ...props
}: TrackingTagProps) => {
  const mergedClassNames = cnTwMerge(
    tagVariants({ variant }),
    isLoading && "skeleton-box w-24",
    className,
  );
  const commonProps = {
    "data-testid": dataTestId,
    ...props,
  };

  return asButton ? (
    <button
      className="inline-flex border-2 border-transparent hover:border-blue-500 rounded-small p-2"
      type="button"
      {...(commonProps as React.ComponentProps<"button">)}
    >
      <span className={mergedClassNames}>{children}</span>
    </button>
  ) : (
    <span className={mergedClassNames} {...commonProps}>
      {children}
    </span>
  );
};
